import { authAdapter } from '@/app/_auth/auth'
import { useAuth } from '@/app/_auth/auth-hooks'
import { KeycloakAuthAdapter, getKeycloak } from '@/app/_auth/keycloak-auth'
import { AuthStepTemplate } from '@/app/_auth/steps/_common/auth-step-template'
import { EmergenceAnimation } from '@/app/_common/emergence-loader'
import { toast } from '@/components/ui/toaster'
import { useApiBaseUrlQuery } from '@/hooks/api/use-api-base-path-query'
import { useDomainsQuery } from '@/hooks/api/use-domains-query'
import { useEmergenceUserAndStepQuery } from '@/hooks/api/use-emergence-user-and-step-query'
import { useTranslate } from '@tolgee/react'
import React from 'react'
import { AuthStep } from '../auth-step-navigator'

/**
 * Check if using Keycloak and not authenticated
 */
function isKeycloakNotAuthenticated(): boolean {
  if (authAdapter instanceof KeycloakAuthAdapter) {
    const isAuth = authAdapter.isAuthenticated()
    return !isAuth
  }
  return false
}

/**
 * Check if Keycloak initialization has failed
 * Returns true if using Keycloak but initialization has failed
 * Waits for initialization to complete, then checks if Keycloak instance exists
 */
function useKeycloakInitCheck() {
  const [initFailed, setInitFailed] = React.useState(false)
  const [isChecking, setIsChecking] = React.useState(true)

  React.useEffect(() => {
    if (!(authAdapter instanceof KeycloakAuthAdapter)) {
      setIsChecking(false)
      return
    }

    // Wait for initialization to complete (give it time)
    // Keycloak initialization is async and happens in auth.ts
    const checkInit = setTimeout(async () => {
      try {
        const keycloak = getKeycloak()

        // If Keycloak instance is null after waiting, initialization likely failed
        if (keycloak === null) {
          // Try to verify by checking if we can get a token
          // This will throw if initialization failed
          try {
            await authAdapter.getToken()
            // If we get here, initialization succeeded but user is not authenticated
            // This is fine - we'll handle that separately
          } catch (error) {
            // Check if this is an initialization error vs just not authenticated
            const errorMessage = error instanceof Error ? error.message : String(error)
            const isInitError =
              errorMessage.includes('Keycloak') &&
              (errorMessage.includes('initialization') ||
                errorMessage.includes('not initialized') ||
                errorMessage.includes('Missing required'))

            if (isInitError) {
              setInitFailed(true)
            }
          }
        }
      } catch (error) {
        // If we can't check, assume initialization failed to be safe
        setInitFailed(true)
      } finally {
        setIsChecking(false)
      }
    }, 1000) // Wait 1 second for initialization to complete

    return () => clearTimeout(checkInit)
  }, [])

  return { initFailed, isChecking }
}

/**
 * Custom hook to handle API base URL fetching and state management
 */
function useBaseUrlStep(shouldFetch: boolean) {
  const { setApiBaseUrl, apiBaseUrl, setStep } = useAuth()

  const query = useApiBaseUrlQuery({
    staleTime: Infinity,
    retry: 3,
    enabled: shouldFetch,
  })

  // Handle all side effects in a single effect
  React.useEffect(() => {
    // Set API base URL when fetched
    if (query.data && apiBaseUrl !== query.data) {
      setApiBaseUrl(query.data)
      return
    }

    // Handle error
    if (query.isError) {
      console.error('BaseUrlFetch error:', query.error)
      setStep(AuthStep.Error)
    }
  }, [query.data, query.isFetching, query.isError, query.error, apiBaseUrl, setApiBaseUrl, setStep])

  return query
}

/**
 * Custom hook to handle user and step data fetching and state management
 */
function useUserAndStepStep(baseUrlReady: boolean) {
  const { setUser, setStep } = useAuth()

  const query = useEmergenceUserAndStepQuery({
    retry: 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff: 1s, 2s, 4s, 5s, 5s
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    enabled: baseUrlReady,
  })

  // Handle all side effects in a single effect
  React.useEffect(() => {
    // Handle success
    if (query.data && !query.isError) {
      setUser(query.data.user)
      if (query.data.nextStep !== null) {
        setStep(query.data.nextStep)
      }
      return
    }

    // Handle error
    if (query.isError) {
      console.error('UserAndStepFetch error:', query.error)

      const isAuthError =
        query.error instanceof Error &&
        (query.error.message === 'AUTHENTICATION_ERROR' ||
          query.error.message === 'KEYCLOAK_NOT_AUTHENTICATED' ||
          query.error.message === 'LOGGED_IN_USER_NOT_FOUND' ||
          query.error.message === 'FAILED_TO_GET_ACCESS_TOKEN' ||
          query.error.message === 'FAILED_TO_GET_KEYCLOAK_TOKEN' ||
          query.error.name === 'AUTHENTICATION_ERROR')

      if (isAuthError) {
        authAdapter?.signOut().catch((err: unknown) => {
          console.error('Failed to sign out:', err)
        })
        toast.error('Session expired. Please login again.')

        // If Keycloak is not authenticated, redirect to login instead of error
        if (isKeycloakNotAuthenticated()) {
          setStep(AuthStep.Login)
        } else {
          setStep(AuthStep.Error)
        }
      } else {
        toast.error('Error fetching account information. Please login again.')
        setStep(AuthStep.Error)
      }
    }
  }, [query.data, query.isFetching, query.isError, query.error, baseUrlReady, setUser, setStep])

  return query
}

/**
 * Custom hook to handle domains fetching and state management
 */
function useDomainsStep(userDataReady: boolean) {
  const { setStep } = useAuth()

  const query = useDomainsQuery({
    retry: 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff: 1s, 2s, 4s, 5s, 5s
    refetchOnMount: 'always',
    enabled: userDataReady,
  })

  // Handle all side effects in a single effect
  React.useEffect(() => {
    // Handle success
    if (query.data && !query.isError) {
      // Check if Keycloak is still authenticated before clearing auth step
      if (isKeycloakNotAuthenticated()) {
        setStep(AuthStep.Login)
        return
      }

      setStep(null) // Clear auth step to show main app
      return
    }

    // Handle error
    if (query.isError) {
      console.error('DomainsFetch error:', query.error)
      setStep(AuthStep.Error)
    }
  }, [query.data, query.isFetching, query.isError, query.error, setStep])

  return query
}

export function GetSessionDataStep() {
  const { t } = useTranslate()
  const { apiBaseUrl, step, setStep } = useAuth()
  const { initFailed, isChecking } = useKeycloakInitCheck()

  // Check if Keycloak initialization has failed - if so, redirect to login immediately
  React.useEffect(() => {
    if (!isChecking && initFailed) {
      toast.error('Authentication initialization failed. Please try logging in again.')
      setStep(AuthStep.Login)
    }
  }, [isChecking, initFailed, setStep])

  // For VPC/Keycloak mode: if user is not authenticated after init completes, skip session data fetch and go to login
  React.useEffect(() => {
    if (!isChecking && !initFailed && isKeycloakNotAuthenticated()) {
      setStep(AuthStep.Login)
    }
  }, [isChecking, initFailed, setStep])

  // Step 1: Fetch API base URL (disabled if Keycloak init failed or still checking or not authenticated)
  const shouldFetchSessionData = !isChecking && !initFailed && !isKeycloakNotAuthenticated()
  const baseUrlQuery = useBaseUrlStep(shouldFetchSessionData)
  const baseUrlReady = Boolean(baseUrlQuery.data && apiBaseUrl)

  // Step 2: Fetch user and step data (disabled if Keycloak init failed or still checking)
  const userAndStepQuery = useUserAndStepStep(baseUrlReady && !isChecking && !initFailed)
  const userDataReady = Boolean(userAndStepQuery.data && !userAndStepQuery.isError)

  // Step 3: Fetch domains (disabled if Keycloak init failed or still checking)
  useDomainsStep(userDataReady && !isChecking && !initFailed)

  // Check Keycloak authentication state after all steps complete
  React.useEffect(() => {
    // Only check if we've completed all steps but haven't cleared the auth step yet
    if (
      baseUrlReady &&
      userDataReady &&
      userAndStepQuery.data &&
      !userAndStepQuery.isError &&
      step === AuthStep.GetSessionData &&
      !isChecking &&
      !initFailed
    ) {
      // Small delay to ensure all state updates have completed
      const checkAuth = setTimeout(() => {
        if (isKeycloakNotAuthenticated()) {
          setStep(AuthStep.Login)
        }
      }, 100)

      return () => clearTimeout(checkAuth)
    }
  }, [
    baseUrlReady,
    userDataReady,
    userAndStepQuery.data,
    userAndStepQuery.isError,
    step,
    setStep,
    isChecking,
    initFailed,
  ])

  return (
    <AuthStepTemplate noContainerStyles hideLogo>
      <EmergenceAnimation title={t('auth.steps.loading.a11y')} />
    </AuthStepTemplate>
  )
}
